import React, { useEffect, useMemo, useState } from "react";

import { enqueueSnackbar } from "notistack";
import { useParams, useSearchParams } from "react-router-dom";
import { SubFolder } from "@/taskpane/types/folder";
import { useFileDnd } from "@/taskpane/hooks/file-dnd";
import { UploadableFile } from "@/taskpane/types/file";
import DragAndDrop from "@/taskpane/components/drag-and-drop/DragAndDrop";
import FolderPageLayout from "@/taskpane/modules/folder/FolderPageLayout";
import { Box, Card, Stack, SxProps, Theme, Typography } from "@mui/material";
import CheckableFile from "@/taskpane/components/checkable-file/CheckableFile";

import { styles } from "./FolderDragAndDropPage.styles";
import { GradientBorder } from "@/taskpane/components/styled-container/gradient-wrapper";
import { useGetSubFoldersQuery } from "@/taskpane/services/folders.hook";
import GradientText from "@/taskpane/components/styled-container/GradientText";

export default function FolderDragAndDropPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [selectedSubFolder, setSelectedSubFolder] = useState<SubFolder>();
  const [selectedFiles, setSelectedFiles] = useState<(UploadableFile & { mimeTypeAllow: boolean })[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<(UploadableFile & { mimeTypeAllow: boolean })[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const { onDrop, files } = useFileDnd((files) => setSelectedFiles((prev) => [...prev, ...files]));
  const [uploadProgress, setUploadProgress] = useState({});
  
  const sousDossierId = searchParams.get("sousDossierId");

  const { data: subFolders, isLoading: subFoldersLoading } = useGetSubFoldersQuery(params.folderId || "", {
    enabled: !!params.folderId,
  });

  useEffect(() => {
    // Don't do anything if data is loading, not present, or if a folder is already selected.
    if (subFoldersLoading || !subFolders || selectedSubFolder) {
      return;
    }

    let folderToSelect: SubFolder | undefined;

    // If there's an ID in the URL, try to find that subfolder.
    if (sousDossierId && subFolders.length === 1) {
      folderToSelect = subFolders[0];
    } else if (sousDossierId && subFolders.length > 1) {
      folderToSelect = subFolders.find((subFolder) => subFolder.sous_dossier_id === sousDossierId);
      // Otherwise, default to the first subfolder in the list.
    }
    // If we found a folder to select, update the state.
    if (folderToSelect) {
      setSelectedSubFolder(folderToSelect);
    }
  }, [subFolders, sousDossierId, selectedSubFolder, subFoldersLoading]);


  const renderFileList = useMemo(() => {
    return files.map((file) => {
      return (
        <CheckableFile
          key={file.id}
          attachments={[file]}
          uploaded={uploadProgress && (uploadProgress as Record<string, {status: string}>)[file.id]?.status === "success"}
          loading={uploadProgress && (uploadProgress as Record<string, {status: string}>)[file.id]?.status === "uploading"}
          selectedAttachments={selectedFiles}
          setSelectedAttachments={setSelectedFiles}
        />
      );
    });
  }, [files, uploadProgress, selectedFiles, setSelectedFiles]);


  

  return (
      <FolderPageLayout
        folderId={params.folderId}
        onSelectSubFolder={setSelectedSubFolder}
        selectedSubfolder={selectedSubFolder}
        files={selectedFiles}
        canValidate={!!selectedSubFolder && selectedFiles?.length > 0}
        onValidate={() => {
          setUploadLoading(false);
          setUploadedFiles([...uploadedFiles, ...selectedFiles]);
          setSelectedFiles([]);
          setSelectedSubFolder(undefined);
        }}
        onUpload={() => {
          setUploadLoading(true);
        }}
        setUploadProgress={setUploadProgress}
        subFolders={subFolders}
      >
        <Stack sx={styles.root}>
          <Typography sx={styles.title}>
              Sélection des fichiers déposés :
            {/* <GradientText>
            </GradientText> */}
          </Typography>
          <GradientBorder>
            <Card sx={{ ...styles.card, ...(uploadLoading ? {} : styles.cardActive) } as SxProps<Theme>}>
            {files?.length === 0 ? (
              <Box sx={styles.dragDropContainer}>
                <DragAndDrop
                  options={{
                    multiple: true,
                    onDrop,
                    onError: (error) => {
                      console.error(error);
                      enqueueSnackbar(`Une erreur est survenue lors de la dépose des fichiers: ${error?.message}`, {
                        variant: "error",
                      });
                    },
                  }}
                />
              </Box>
            ) : (
              <>
              <Stack sx={styles.fileListContainer}>
                <Stack sx={styles.fileList}>
                  {renderFileList}
                </Stack>
                <Box sx={styles.foldedDragDrop}>
                  <DragAndDrop
                    folded
                    options={{
                      multiple: true,
                      onDrop,
                      onError: (error) => {
                        console.error(error);
                        enqueueSnackbar(`Une erreur est survenue lors de la dépose des fichiers: ${error?.message}`, {
                          variant: "error",
                        });
                      },
                    }}
                    />
                </Box>
                </Stack>
              </>
            )}
          </Card>
          </GradientBorder>
        </Stack>
      </FolderPageLayout>
  );
}
